/**
 * echo's fleet API (/api/fleet/*), as the screens read it.
 *
 * These mirror Contract C (docs/superpowers/plans/2026-09-30-fleet-admin-00-overview.md) and echo's
 * backend/echo/src/fleet/types.ts field for field. A value echo could not load arrives as `null`
 * plus an entry in `warnings`; the screens show it as "unavailable", never as 0.
 */

/** Every `error` code echo's fleet routes answer with (Contract C, error table). */
export const FLEET_ERROR_CODES = [
  'traccar_not_connected',
  'traccar_unavailable',
  'traccar_auth',
  'milo_not_connected',
  'milo_unavailable',
  'once_not_connected',
  'once_unavailable',
  'once_auth',
  'not_found',
  'validation',
  'rebuild_running',
  'sync_running',
] as const;

export type FleetErrorCode = (typeof FLEET_ERROR_CODES)[number];

/** The error body echo sends: `{ error, message }`, plus `data` on `rebuild_running`. */
export interface FleetErrorBody {
  error: string;
  message?: string;
  data?: unknown;
}

/** The parts a read could not load (echo's `FleetWarning`); their fields are null. */
export const FLEET_WARNINGS = [
  'accounts',
  'links',
  'groups',
  'devices',
  'positions',
  'dataUsage',
  'milo',
  'ledgerStatus',
  'ledgerCoverage',
  'statistics',
  'zoneSharing',
  'customers',
] as const;

export type FleetWarning = (typeof FLEET_WARNINGS)[number];

/** Every read, and every write's answer: `{ data, warnings }` (echo's `ReadEnvelope`). */
export interface FleetResult<T> {
  data: T;
  /** Typed loosely on purpose: a part echo adds later is shown by its id rather than dropped. */
  warnings: string[];
}

export interface NamedRef {
  id: number;
  name: string;
}

/* ── Accounts ─────────────────────────────────────────────────────────── */

export type AccountRole = 'admin' | 'manager' | 'user' | 'readonly';

/** `{ enabled: false, dailyLimit: null, questionsToday: 0 }` for an account Milo has no row for. */
export interface AccountMilo {
  enabled: boolean;
  dailyLimit: number | null;
  questionsToday: number;
}

export interface AccountRow {
  id: number;
  name: string;
  email: string;
  role: AccountRole;
  disabled: boolean;
  expirationTime: string | null;
  deviceCount: number | null;
  groupCount: number | null;
  /** null when GeoPoint AI (Milo) could not be read. */
  milo: AccountMilo | null;
}

export interface AccountsView {
  cards: { total: number; disabled: number; adminsAndManagers: number; miloOn: number | null };
  rows: AccountRow[];
}

export interface AccountDetail extends AccountRow {
  devices: NamedRef[] | null;
  groups: NamedRef[] | null;
}

export interface AccountMiloInput {
  enabled: boolean;
  /** null = the default daily limit from GeoPoint AI settings. */
  dailyLimit: number | null;
}

/** POST/DELETE /links: exactly one of deviceId / groupId. */
export type LinkInput = { userId: number; deviceId: number } | { userId: number; groupId: number };

/* ── Vehicles ─────────────────────────────────────────────────────────── */

export type DeviceStatus = 'online' | 'offline' | 'unknown';
export type FuelQuality = 'trusted' | 'approx' | 'none';

export interface DeviceRow {
  id: number;
  name: string;
  uniqueId: string;
  model: string | null;
  groupId: number | null;
  groupName: string | null;
  status: DeviceStatus;
  lastUpdate: string | null;
  lastFixTime: string | null;
  speedLimitKmh: number | null;
  fuelQuality: FuelQuality | null;
  /** Traccar's totalDistance accumulator of the latest record, km: what the accumulators save changes. */
  odometerKm: number | null;
  /** Engine hours of the latest record, h. */
  hours: number | null;
  /** Accounts linked to the vehicle directly (the links link/unlink changes). */
  accountCount: number | null;
  /** Bytes sent today and the 6 days before. */
  dataBytes7d: number | null;
}

export interface DevicesView {
  cards: {
    total: number;
    onlineNow: number;
    silent24h: number;
    noFixToday: number | null;
    dataTodayBytes: number | null;
  };
  rows: DeviceRow[];
}

export interface LastPosition {
  fixTime: string;
  latitude: number;
  longitude: number;
  speedKmh: number;
  valid: boolean;
}

export interface DeviceDetail extends DeviceRow {
  accounts: NamedRef[] | null;
  /** Newest first, at most 20. */
  lastPositions: LastPosition[] | null;
}

export interface DeviceSettingsInput {
  speedLimitKmh?: number | null;
  fuelQuality?: FuelQuality | null;
}

export interface DeviceAccumulatorsInput {
  odometerKm?: number;
  hours?: number;
}

/* ── Groups and zones ─────────────────────────────────────────────────── */

export interface GroupRow {
  id: number;
  name: string;
  deviceCount: number | null;
  accountCount: number | null;
}

export interface GroupsView {
  rows: GroupRow[];
}

export interface GroupDetail extends GroupRow {
  devices: NamedRef[] | null;
  accounts: NamedRef[] | null;
}

export interface ZoneRow {
  id: number;
  name: string;
  description: string | null;
  /** Traccar WKT: `CIRCLE (lat lon, r)`, `POLYGON ((lat lon, …))`, `LINESTRING (lat lon, …)`. */
  area: string;
  sharedWith: { accounts: NamedRef[]; groups: NamedRef[] } | null;
}

export interface ZonesView {
  rows: ZoneRow[];
}

/* ── Ledger and health ────────────────────────────────────────────────── */

export interface LedgerStatus {
  running: boolean;
  done: number;
  total: number;
  /** `YYYY-MM-DD`, the ledger's first day (`ledger.start`). */
  coverageStart: string;
}

export interface LedgerCoverageRow {
  deviceId: number;
  /** null for a device the ledger stored but Traccar no longer has. */
  name: string | null;
  days: string[];
}

export interface LedgerView {
  /** null when the status could not be read (or the ledger is switched off). */
  status: LedgerStatus | null;
  /** Every vehicle that has reported, with the days stored for it (possibly none). */
  coverage: LedgerCoverageRow[] | null;
  from: string;
  to: string;
}

export interface RebuildInput {
  deviceId: number;
  from: string;
  to: string;
}

export interface SilentDevice {
  id: number;
  name: string;
  lastUpdate: string | null;
}

export interface DataUsageRow {
  deviceId: number;
  name: string | null;
  days: { date: string; bytes: number }[];
}

export interface StatisticsDay {
  /** The UTC day the counts cover (Traccar splits its statistics at UTC midnight). */
  date: string;
  messagesReceived: number;
  messagesStored: number;
  activeDevices: number;
}

export interface HealthView {
  /** No record for more than 24 h (or never). */
  silent: SilentDevice[];
  dataUsage: DataUsageRow[] | null;
  statistics: StatisticsDay[] | null;
}

/* ── GeoPoint AI (Milo) ───────────────────────────────────────────────── */

export const MILO_PROVIDERS = ['xai', 'google', 'anthropic'] as const;
export type MiloProvider = (typeof MILO_PROVIDERS)[number];

/** USD per 1M tokens. */
export interface MiloPrice {
  input: number;
  cachedInput: number;
  output: number;
  /** What writing to the cache costs (Anthropic bills it apart); absent = billed at `input`. Never `null`, never `0` for "none". */
  cacheWrite?: number;
}

/** Milo's `SettingsStore.toPublic`: a key is never sent back, only whether one is set and its last 4. */
export interface MiloSettings {
  providers: Record<MiloProvider, { configured: boolean; keyLast4: string | null; model: string; effort: string }>;
  primary: MiloProvider;
  fallback: MiloProvider | null;
  monthlyCapUsd: number;
  defaultDailyLimit: number;
  prices: Record<string, MiloPrice>;
}

/** PUT body: `apiKey` left out keeps the stored key, `""` removes it, anything else replaces it. */
export interface MiloSettingsInput {
  providers: Record<MiloProvider, { apiKey?: string; model: string; effort: string }>;
  primary: MiloProvider;
  fallback: MiloProvider | null;
  monthlyCapUsd: number;
  defaultDailyLimit: number;
  prices: Record<string, MiloPrice>;
}

export interface MiloKeyTest {
  ok: boolean;
  detail: string;
  zdr: boolean | null;
  models: string[];
}

export interface MiloAccessRow {
  userId: number;
  name: string | null;
  email: string | null;
  /** Milo still has data for this Traccar user id, but the account is gone. */
  deleted: boolean;
  enabled: boolean;
  dailyLimit: number | null;
  questionsToday: number;
  questionsMonth: number;
  costMonthUsd: number;
  /** The account's customer (its top manager, or itself); null for a deleted account. */
  customerId: number | null;
}

/** A customer's own model: all three set, or the customer runs on the global settings. */
export interface MiloCustomerModel {
  provider: MiloProvider;
  model: string;
  effort: string;
}

/**
 * One customer (a manager account and the logins it manages, or a standalone login) with its own
 * GeoPoint AI model and monthly budget. `provider`, `model` and `effort` are all null (the global
 * settings apply) or all set; `monthlyBudgetUsd` null = no budget of its own (only the global cap).
 */
export interface MiloCustomerRow {
  customerId: number;
  name: string | null;
  email: string | null;
  /** Milo has a row for this customer, but its account is gone. */
  deleted: boolean;
  /** Listed accounts whose customer this is. */
  memberCount: number;
  /** Of those, the ones with GeoPoint AI on. */
  enabledMembers: number;
  provider: MiloProvider | null;
  model: string | null;
  effort: string | null;
  monthlyBudgetUsd: number | null;
  costMonthUsd: number;
  questionsMonth: number;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** PUT /milo/customers/:id — both null removes the customer's own settings. */
export interface MiloCustomerInput {
  model: MiloCustomerModel | null;
  monthlyBudgetUsd: number | null;
}

export interface MiloUsage {
  month: string;
  capUsd: number;
  spentUsd: number;
  rows: {
    userId: number;
    name: string | null;
    questions: number;
    errors: number;
    costUsd: number;
    inputTokens: number;
    cachedTokens: number;
    outputTokens: number;
  }[];
}

export type MiloErrorKind = 'timeout' | 'provider' | 'tools' | 'aborted' | 'other';

export interface MiloConversationItem {
  kind: 'conversation';
  id: string;
  userId: number;
  accountName: string | null;
  title: string;
  startedAt: string;
  lastAt: string;
  turns: number;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number;
  models: string[];
  fallback: boolean;
  errors: number;
  durationMs: number;
  /** 👍 / 👎 on this conversation's answers. */
  feedback: { up: number; down: number };
}

export interface MiloFailedItem {
  kind: 'failed';
  id: string;
  userId: number;
  accountName: string | null;
  conversationId: string | null;
  question: string;
  errorKind: MiloErrorKind;
  provider: string;
  model: string;
  createdAt: string;
  costUsd: number;
  durationMs: number;
}

export type MiloListItem = MiloConversationItem | MiloFailedItem;

export interface MiloConversationList {
  items: MiloListItem[];
  nextCursor: string | null;
}

export type MiloStatusFilter = 'all' | 'ok' | 'error';

export type MiloRating = 'up' | 'down';

export interface MiloConversationFilters {
  userId?: number;
  from?: string;
  to?: string;
  status?: MiloStatusFilter;
  tool?: string;
  /** 'down' = only conversations with at least one 👎 (failed questions are left out). */
  feedback?: 'down';
}

export interface MiloRef {
  kind: string;
  path: string;
  label: string;
}

export interface MiloMessage {
  id: string;
  role: 'user' | 'assistant';
  createdAt: string;
  /**
   * Milo's stored `ui_parts`, as they are. Two kinds matter: `{ type: 'text', text }` and
   * `{ type: 'data-refs', data: { refs: { r1: MiloRef } } }` (the link chips); anything else shows
   * as nothing. `src/lib/fleet/answer.ts` reads them.
   */
  parts: unknown[];
  /** The user's 👍 / 👎 on an answer; null when not rated (always null on a question). */
  feedback: MiloRating | null;
}

export interface MiloToolCall {
  name: string;
  input: unknown;
  output: unknown;
}

export interface MiloTurn {
  messageId: string | null;
  createdAt: string;
  provider: string;
  model: string;
  fallback: boolean;
  /** 'aborted' = the user left while the model was answering: a question, not an error. */
  status: 'ok' | 'error' | 'aborted';
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number;
  durationMs: number;
  /** `"0:step,812:reason,…"` — milliseconds since the question, then what happened. */
  timeline: string;
  toolCalls: MiloToolCall[];
}

export interface MiloConversation {
  id: string;
  userId: number;
  accountName: string | null;
  title: string;
  startedAt: string;
  messages: MiloMessage[];
  turns: MiloTurn[];
  failed: MiloFailedItem[];
}

export interface MiloFailedTurn extends MiloFailedItem {
  partialAnswer: string | null;
}

export interface MiloInsights {
  days: {
    date: string;
    questions: number;
    errors: number;
    costUsd: number;
    inputTokens: number;
    cachedTokens: number;
    outputTokens: number;
    /** Ratings given that day (by when they were last set). */
    up: number;
    down: number;
  }[];
  byUser: { userId: number; name: string | null; questions: number; errors: number; costUsd: number }[];
  totals: {
    questions: number;
    errors: number;
    errorRate: number;
    fallbackRate: number;
    avgDurationMs: number;
    p95DurationMs: number;
    cacheHitRate: number;
    costUsd: number;
  };
  tools: { name: string; calls: number; errors: number }[];
  /** Per provider + model over the range, most questions first. */
  byModel: MiloModelStats[];
}

export interface MiloModelStats {
  provider: string;
  model: string;
  /** `ok` + `aborted` turns. */
  questions: number;
  avgSeconds: number;
  p90Seconds: number;
  costPerQuestionUsd: number;
}

/** A `from`/`to` pair of admin-local dates, `YYYY-MM-DD`. */
export interface DateRange {
  from: string;
  to: string;
}

/* ── SIM data (1NCE) — echo's src/fleet/types.ts, field for field ──────── */

export interface SimRunView {
  trigger: 'schedule' | 'catch-up' | 'manual';
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'ok' | 'partial' | 'failed';
  sims: number;
  daysChanged: number;
  error: string | null;
}

/** One SIM. MB are decimal strings as 1NCE bills them; `vehicle` null = not in a vehicle (or Traccar unavailable). */
export interface SimRow {
  iccid: string;
  /** The SIM's phone number: where SMS commands to the tracker go. */
  msisdn: string | null;
  imsi: string | null;
  /** The device's IMEI(SV) as 1NCE sees it. */
  imei: string | null;
  ipAddress: string | null;
  activationDate: string | null;
  label: string | null;
  status: string | null;
  vehicle: NamedRef | null;
  quotaTotalMb: string | null;
  quotaLeftMb: string | null;
  quotaCheckedAt: string | null;
  quotaExpiresAt: string | null;
  sessionState: 'open' | 'closed' | 'unknown';
  sessionSince: string | null;
  usedMb: string;
  averageMb: string;
  daysChecking: number;
  lastDayWithData: string | null;
  lastError: string | null;
  /** The stored days in the range, oldest first (days with no row are not listed). */
  days: SimDaySeriesPoint[];
}

export interface SimDaySeriesPoint {
  day: string;
  volumeMb: string;
  state: SimDayState;
}

export interface SimsView {
  from: string;
  to: string;
  live: boolean;
  checkedAt: string | null;
  lastRun: SimRunView | null;
  running: boolean;
  rows: SimRow[];
}

export type SimDayState = 'confirmed' | 'checking' | 'changed';

export interface SimDayView {
  day: string;
  volumeMb: string;
  txMb: string;
  rxMb: string;
  sms: number;
  state: SimDayState;
  changeMb: string | null;
}

export interface SimDaysView {
  iccid: string;
  from: string;
  to: string;
  days: SimDayView[];
}
